import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import ReactToPrint from "react-to-print";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Nav, NavItem, NavLink } from "reactstrap";
import { Card, CardBody, PrintPage, GlobalStyle, ShowInPrintOnly } from "./styles";
import Filters from "./components/Filters";
import Kpis from "./components/Kpis";
import SucursalTable from "./components/SucursalTable";
import ClienteTable from "./components/ClienteTable";
import ClienteResumen from "./components/ClienteResumen";
import Breadcrumb from "../../../components/common/breadcrumb";
import BarChart from "./components/BarChart";
import PieChart from "./components/PieChart";
import useFetcher from "../../../hooks/useFetcher";
import { fetchReporting } from "../../../services/reportingServices";
import PmcTable from "./components/PmcTable";
//
const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

const sixMonthsAgo = new Date(currentDate);
sixMonthsAgo.setMonth(currentDate.getMonth() - 6);

const formattedDate = (date) => date.toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });

const initialFilters = {
  startDate: `${formattedDate(sixMonthsAgo.getMonth() + 1)}-${formattedDate(sixMonthsAgo.getFullYear())}`,
  endDate: `${formattedDate(currentMonth)}-${formattedDate(currentYear)}`,
};

const initialFrontFilters = {
  clase: ["alerta", "tarea"],
  type: ["sucursal", "nominas", "facturacion", "cobranzas"],
};

const initialGroupBy = "month";

const meses = {
  "01": "Enero",
  "02": "Febrero",
  "03": "Marzo",
  "04": "Abril",
  "05": "Mayo",
  "06": "Junio",
  "07": "Julio",
  "08": "Agosto",
  "09": "Septiembre",
  10: "Octubre",
  11: "Noviembre",
  12: "Diciembre",
};

const initialActiveTab = "Tareas y Alertas";

const axisBottomLabel = {
  month: "MESES",
  quarter: "TRIMESTRES",
  year: "AÑOS",
};

const Component = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [frontFilters, setFrontFilters] = useState(initialFrontFilters);
  const [tableFilters, setTableFilters] = useState(initialFilters);
  const [groupBy, setGroupBy] = useState(initialGroupBy);
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  // eslint-disable-next-line no-unused-vars
  const [data, isLoading, error, setData, fetchData] = useFetcher(fetchReporting, [{ ...filters }]);
  const [selectedColumn, setSelectedColumn] = useState();
  const componentRef = React.useRef();

  const resetSelectedColumn = (filterAddedKey) => {
    const filtersThatResetTheColumn = ["startDate", "endDate"];
    if (filtersThatResetTheColumn.includes(filterAddedKey)) {
      setSelectedColumn("");
    }
  };

  const addFilter = (addedFilters) => {
    const updatedFilters = { ...filters };

    addedFilters.forEach((addedFilter) => {
      resetSelectedColumn(addedFilter.key);
      updatedFilters[addedFilter.key] = addedFilter.value;
    });

    setFilters(updatedFilters);
    return updatedFilters;
  };

  const removeFilter = (key) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[key];
    setFilters(updatedFilters);
  };

  const addFrontFilter = async (filter) => {
    const newFilters = () => {
      const updatedFilters = { ...frontFilters };

      // Set the filter value
      updatedFilters[filter.key] = filter.value;

      return updatedFilters;
    };

    await setFrontFilters(newFilters());
    return newFilters();
  };

  const handleFilterByType = async (filter) => {
    const newFilters = () => {
      const updatedFilters = { ...frontFilters };

      if (updatedFilters[filter.key] && updatedFilters[filter.key].includes(filter.value)) {
        updatedFilters[filter.key] = updatedFilters[filter.key].filter((val) => val !== filter.value);
      } else {
        updatedFilters[filter.key] = [...(updatedFilters[filter.key] ? updatedFilters[filter.key] : []), filter.value];
      }

      return updatedFilters;
    };

    await setFrontFilters(newFilters());
    return newFilters();
  };

  // Función para obtener el nombre del mes
  const getMonthName = (month) => {
    return meses[month];
  };

  // Función para obtener el nombre del trimestre
  const getQuarterName = (month) => {
    return `Q${Math.ceil(Number(month) / 3)}`;
  };

  // Función para calcular el valor total por estado
  const calculateTotalValueByKey = (groupedData, key) => {
    return Object.values(groupedData).reduce((total, group) => total + group[key], 0);
  };

  // Función para filtrar por type y class
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      return !!frontFilters.clase && frontFilters.clase.includes(item.class) && !!frontFilters.type && frontFilters.type.includes(item.type.toLowerCase());
    });
  }, [data, filters, frontFilters]);

  // Función para agrupar por mes, trimestre o año
  const groupedData = useMemo(() => {
    return filteredData.reduce((acc, item) => {
      let groupKey = "";

      if (groupBy === "month") {
        groupKey = `${item.mes}-${item.año}`;
      } else if (groupBy === "quarter") {
        const quarter = Math.ceil(Number(item.mes) / 3);
        groupKey = `${item.año}-Q${quarter}`;
      } else if (groupBy === "year") {
        groupKey = item.año;
      }

      if (!acc[groupKey]) {
        acc[groupKey] = {
          month: getMonthName(item.mes),
          mes: item.mes,
          año: item.año,
          year: item.año,
          quarter: getQuarterName(item.mes),
          vencidos: 0,
          pendientes: 0,
          resueltos: 0,
        };
      }

      // Create a new object for the group and modify it
      const updatedGroup = {
        ...acc[groupKey],
        vencidos: acc[groupKey].vencidos + item.vencidos,
        pendientes: acc[groupKey].pendientes + item.pendientes,
        resueltos: acc[groupKey].resueltos + item.resueltos,
      };

      // Update the acc object with the updated group
      acc[groupKey] = updatedGroup;

      return acc;
    }, {});
  }, [filteredData]);

  // Función para formatear datos para el barchart
  const formattedBarChartData = useMemo(() => {
    return Object.values(groupedData).map((group) => {
      return group;
    });
  }, [groupedData]);

  // Función para formatear datos para el piechart
  const formattedPieChartData = useMemo(() => {
    return [
      {
        id: "vencidos",
        label: "Vencidos",
        value: calculateTotalValueByKey(groupedData, "vencidos"),
      },
      {
        id: "pendientes",
        label: "Pendientes",
        value: calculateTotalValueByKey(groupedData, "pendientes"),
      },
      {
        id: "resueltos",
        label: "Resueltos",
        value: calculateTotalValueByKey(groupedData, "resueltos"),
      },
    ];
  }, [groupedData]);

  // Función para formatear datos para el piechart
  const formattedKpisData = useMemo(() => {
    // Función para filtrar por type y class
    const kpisFilteredData = data.filter((item) => {
      return !!frontFilters.clase && frontFilters.clase.includes(item.class);
    });

    const totalByKpi = kpisFilteredData.reduce((acc, item) => {
      const groupKey = item.type.toLowerCase();

      if (!acc[groupKey]) {
        acc[groupKey] = 0;
      }

      // Create a new object for the group and modify it
      const updatedGroup = acc[groupKey] + item.vencidos + item.pendientes + item.resueltos;

      // Update the acc object with the updated group
      acc[groupKey] = updatedGroup;

      return acc;
    }, {});

    const resueltosByKpi = kpisFilteredData.reduce((acc, item) => {
      const groupKey = item.type.toLowerCase();

      if (!acc[groupKey]) {
        acc[groupKey] = 0;
      }

      // Create a new object for the group and modify it
      const updatedGroup = acc[groupKey] + item.resueltos;

      // Update the acc object with the updated group
      acc[groupKey] = updatedGroup;

      return acc;
    }, {});

    return [
      { id: "sucursal", color: "17a2b8", kpi: resueltosByKpi.sucursal, total: totalByKpi.sucursal, icon: "fa-solid fa-shop", label: "Sucursal" },
      { id: "nominas", color: "17a2b8", kpi: resueltosByKpi.nominas, total: totalByKpi.nominas, icon: "fa-solid fa-user", label: "Nóminas" },
      {
        id: "facturacion",
        color: "17a2b8",
        kpi: resueltosByKpi.facturacion,
        total: totalByKpi.facturacion,
        icon: "fa-solid fa-file-invoice-dollar",
        label: "Facturación",
      },
      {
        id: "cobranzas",
        color: "17a2b8",
        kpi: resueltosByKpi.cobranzas,
        total: totalByKpi.cobranzas,
        icon: "fa-solid fa-hand-holding-dollar",
        label: "Cobranzas",
      },
    ];
  }, [filteredData, groupedData]);

  useEffect(() => {
    const mergedFilters = { ...filters, ...frontFilters };

    if (mergedFilters.clase.length > 1) {
      delete mergedFilters.clase;
    }

    if (selectedColumn) {
      let startDate = `${selectedColumn.data.mes}-${selectedColumn.data.año}`;
      let endDate = `${selectedColumn.data.mes}-${selectedColumn.data.año}`;

      if (groupBy === "month") {
        startDate = `${selectedColumn.data.mes}-${selectedColumn.data.año}`;
        endDate = `${Number(selectedColumn.data.mes).toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false })}-${selectedColumn.data.año}`;
      } else if (groupBy === "quarter") {
        startDate = `${selectedColumn.data.mes}-${selectedColumn.data.año}`;
        endDate = `${(Number(selectedColumn.data.mes) + 2).toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false })}-${selectedColumn.data.año}`;
      } else if (groupBy === "year") {
        startDate = `${selectedColumn.data.mes}-${selectedColumn.data.año}`;
        endDate = `${(Number(selectedColumn.data.mes) + 11).toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false })}-${selectedColumn.data.año}`;
      }

      setTableFilters({ ...mergedFilters, startDate, endDate });
    }
  }, [filters, frontFilters, selectedColumn]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData();
    }, 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchData]);

  return (
    <>
      <GlobalStyle />

      <Helmet>
        <title>PIN - Sistema de Reportes</title>
      </Helmet>

      <Breadcrumb title="Reporting" parent="Calendario" />

      <div ref={componentRef}>
        <div className="container-fluid page-break">
          <div className="row">
            <div className="col-sm-12">
              <Card className="card">
                <CardBody className="card-body">
                  <Filters currentFilters={filters} initialFilters={initialFilters} groupBy={setGroupBy} addFilter={addFilter} removeFilter={removeFilter} />
                  <Kpis data={formattedKpisData} currentFilters={frontFilters} addFilter={handleFilterByType} removeFilter={removeFilter} />

                  <div className="row">
                    <div className="col-sm-6">
                      <Nav tabs fill>
                        <NavItem>
                          <NavLink
                            active={activeTab === "Tareas y Alertas"}
                            onClick={() => {
                              setActiveTab("Tareas y Alertas");
                              addFrontFilter({ key: "clase", value: ["alerta", "tarea"] });
                            }}
                          >
                            Tareas y Alertas
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            active={activeTab === "Alertas"}
                            onClick={() => {
                              setActiveTab("Alertas");
                              addFrontFilter({ key: "clase", value: ["alerta"] });
                            }}
                          >
                            Alertas
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            active={activeTab === "Tareas"}
                            onClick={() => {
                              setActiveTab("Tareas");
                              addFrontFilter({ key: "clase", value: ["tarea"] });
                            }}
                          >
                            Tareas
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </div>

                    <div className="col-sm-6">
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <ReactToPrint
                          trigger={() => (
                            <PrintPage className="btn btn-tertiary" type="button">
                              <FontAwesomeIcon size="xs" icon="fa-solid fa-download" /> Descargar
                            </PrintPage>
                          )}
                          content={() => componentRef.current}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 20 }} />

                  <div className="row">
                    <div className="col-sm-8">
                      <BarChart
                        data={{
                          keys: ["vencidos", "pendientes", "resueltos"],
                          indexBy: groupBy,
                          axisBottom: {
                            legend: axisBottomLabel[groupBy],
                          },
                          axisLeft: {
                            legend: activeTab.toUpperCase(),
                          },
                          values: formattedBarChartData,
                          onClick: (column) => setSelectedColumn(column),
                        }}
                        isLoading={isLoading}
                      />
                    </div>

                    <div className="col-sm-4">
                      <PieChart
                        data={{
                          keys: ["vencidos", "pendientes", "resueltos"],
                          indexBy: groupBy,
                          axisBottom: {
                            legend: axisBottomLabel[groupBy],
                          },
                          axisLeft: {
                            legend: activeTab.toUpperCase(),
                          },
                          values: formattedPieChartData,
                        }}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>

                  <div style={{ height: 40 }} />

                  {!!selectedColumn && (
                    <div className="row">
                      <div className="col-sm-12">
                        {filters.cliente && <ClienteResumen filters={tableFilters} />}
                        Columna seleccionada: {selectedColumn.indexValue}
                      </div>
                    </div>
                  )}

                  <div style={{ height: 16 }} />

                  <div className="row">
                    {!!selectedColumn && (
                      <div className="col-sm-6">{filters.cliente ? <SucursalTable filters={tableFilters} /> : <ClienteTable filters={tableFilters} />}</div>
                    )}

                    <div className="col-sm-6">
                      {filters.cliente ? (
                        <PmcTable filters={filters} initialActiveTab="table" showTabs={!!selectedColumn} />
                      ) : (
                        <>{!!selectedColumn && <SucursalTable filters={tableFilters} />}</>
                      )}
                    </div>

                    {!selectedColumn && <div className="col-sm-6">{filters.cliente && <PmcTable filters={filters} initialActiveTab="graph" showTabs={false} />}</div>}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>

        <ShowInPrintOnly>
          <div className="container-fluid page-break ">
            <div className="row">
              <div className="col-sm-12">
                <Card className="card">
                  <CardBody className="card-body">
                    <div className="row">
                      <div className="col-sm-12">{filters.cliente ? <PmcTable filters={filters} initialActiveTab="graph" showTabs={false} /> : <></>}</div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          </div>
        </ShowInPrintOnly>
      </div>
    </>
  );
};

export default Component;
